import { Button, Loading } from '@affine/component';
import {
  type HeaderColDef,
  ListHeaderTitleCell,
  type ListItem,
  ListTableHeader,
  VirtualizedList,
} from '@affine/core/components/page-list';
import { useWikiActionsConfig } from '@affine/core/components/page-list/operation-menu-items/wiki-action/use-wiki-action-config';
import { SelectorLayout } from '@affine/core/components/page-list/selector/selector-layout';
import { EmptyList } from '@affine/core/components/page-list/view/edit-collection/select-page';
import { WorkspacePermissionService } from '@affine/core/modules/permissions';
import { useActionDocsWiki, useGetDocsWiki } from '@affine/core/modules/wiki';
import { Trans } from '@affine/i18n';
import type { PostDocsStatusBodyAction } from '@docnboard/rest';
import {
  useLiveData,
  useServices,
  WorkspaceService,
} from '@toeverything/infra';
import { useCallback, useMemo, useState } from 'react';

import * as styles from './styles.css';
import type { ActionsProps } from './types';
import { PageListItemRenderer } from './wiki-doc-item';

const pageHeaderColsDef: HeaderColDef[] = [
  {
    key: 'title',
    content: <ListHeaderTitleCell />,
    flex: 3,
    alignment: 'start',
    sortable: true,
  },
  {
    key: 'status',
    content: <Trans i18nKey="docnboard.wiki.column.status.title" />,
    flex: 2,
    sortable: true,
    alignment: 'center',
  },
  {
    key: 'createdAt',
    content: <Trans i18nKey="Created" />,
    flex: 2,
    alignment: 'center',
  },
  {
    key: 'updatedAt',
    content: <Trans i18nKey="Updated" />,
    flex: 2,
    alignment: 'center',
  },
  {
    key: 'createdBy',
    content: <Trans i18nKey="docnboard.wiki.column.createdBy.title" />,
    flex: 1,
    alignment: 'center',
  },
  {
    key: 'updatedBy',
    content: <Trans i18nKey="docnboard.wiki.column.updatedBy.title" />,
    flex: 1,
    alignment: 'center',
  },
];

export const WikiDocs: React.FC = () => {
  const [selectedDoc, setSelectedDoc] = useState<string[]>([]);

  const { workspaceService, workspacePermissionService } = useServices({
    WorkspaceService,
    WorkspacePermissionService,
  });
  const workspace = workspaceService.workspace;
  const docCollection = workspace.docCollection;
  const isReadOnly = useLiveData(
    workspacePermissionService.permission.isReadOnly$
  );

  const { wikiDocsRes } = useGetDocsWiki(workspace.id);
  const { onActionDocs, isPending } = useActionDocsWiki(workspace.id);

  const onAction = useCallback(
    (actionType: PostDocsStatusBodyAction) => {
      onActionDocs(selectedDoc, actionType, {
        onSuccess: () => actionType === 'delete' && setSelectedDoc([]),
      });
    },
    [onActionDocs, selectedDoc]
  );

  const items = useMemo(
    () => wikiDocsRes.data?.data?.items.map(i => ({ ...i, id: i.docId })) ?? [],
    [wikiDocsRes]
  );

  const pageHeaderRenderer = useCallback(
    () => (
      <div className="min-w-[700px]">
        <ListTableHeader headerCols={pageHeaderColsDef} />
      </div>
    ),
    []
  );

  const pageItemRenderer = useCallback(
    (item: ListItem) => (
      <PageListItemRenderer disabled={isPending} item={item} />
    ),
    [isPending]
  );

  const virtualizedList = useMemo(
    () => (
      <div
        className={styles.pagesTab}
        data-testid="workspace-wiki-selected-docs-list"
      >
        {items.length > 0 ? (
          <VirtualizedList
            className={styles.pageList}
            items={items}
            rowAsLink
            docCollection={docCollection}
            selectable={!isReadOnly}
            onSelectedIdsChange={setSelectedDoc}
            selectedIds={selectedDoc}
            itemRenderer={pageItemRenderer}
            headerRenderer={pageHeaderRenderer}
          />
        ) : (
          <EmptyList />
        )}
      </div>
    ),
    [
      items,
      docCollection,
      pageItemRenderer,
      pageHeaderRenderer,
      selectedDoc,
      isReadOnly,
    ]
  );

  return (
    <div className={styles.selectedDocs}>
      {isReadOnly ? (
        virtualizedList
      ) : (
        <SelectorLayout
          selectedCount={selectedDoc.length}
          actions={
            <Actions
              onAction={onAction}
              disabled={!selectedDoc.length}
              isPending={isPending}
            />
          }
        >
          {virtualizedList}
        </SelectorLayout>
      )}
    </div>
  );
};

const Actions: React.FC<ActionsProps> = ({ onAction, disabled, isPending }) => {
  const actions = useWikiActionsConfig();
  return (
    <div className={styles.actionsContainer}>
      {isPending && <Loading size="28px" />}
      {actions
        .filter(a => a.type !== 'add')
        .map(action => (
          <Button
            key={action.type}
            prefix={action.type !== 'delete' ? action.icon : undefined}
            disabled={disabled || isPending}
            onClick={() => onAction(action.type)}
            variant={action.variant}
            data-testid={action.testId}
          >
            {action.label}
          </Button>
        ))}
    </div>
  );
};
